import { useMemo } from 'react'
import { useInfiniteQuery, keepPreviousData } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { useProtocol } from '@/hooks/useProtocol'
import {
    fetchCrossProtocolTimeline,
    type CrossProtocolCursors,
} from '@/services/crossProtocolService'
import type { PostsPage } from '@/types/posts'

export function useCrossProtocolTimeline(options?: {
    enabled?: boolean
    enabledAccountIds?: string[]
}) {
    const { session, mastodonSession, pixelfedSession } = useAuth()
    const { connectedAccounts } = useProtocol()
    const { effectiveMastodonSession, effectivePixelfedSession, hasFediverse } = useMemo(() => {
        const enabledIds = options?.enabledAccountIds ?? []
        const isAccountEnabled = (platform: 'mastodon' | 'pixelfed') => {
            // Only consider IDs for the relevant platform — bluesky IDs stored for
            // multi-account radio selection must not affect fediverse platform checks
            const platformAccountIds = connectedAccounts
                .filter(a => a.platform === platform)
                .map(a => a.id)
            const enabledForPlatform = enabledIds.filter(id => platformAccountIds.includes(id))
            if (enabledForPlatform.length === 0) return true
            return platformAccountIds.some(id => enabledForPlatform.includes(id))
        }
        const effMastodon = isAccountEnabled('mastodon') ? mastodonSession : null
        const effPixelfed = isAccountEnabled('pixelfed') ? pixelfedSession : null
        return {
            effectiveMastodonSession: effMastodon,
            effectivePixelfedSession: effPixelfed,
            hasFediverse: !!effMastodon || !!effPixelfed,
        }
    }, [connectedAccounts, options?.enabledAccountIds, mastodonSession, pixelfedSession])

    const queryKey = [
        'timeline',
        'cross-protocol',
        session?.did ?? null,
        effectiveMastodonSession?.instance_url ?? null,
        effectivePixelfedSession?.instance_url ?? null,
    ] as const

    const query = useInfiniteQuery<
        PostsPage,
        Error,
        { pages: PostsPage[] },
        typeof queryKey,
        CrossProtocolCursors | undefined
    >({
        queryKey,
        queryFn: async ({ pageParam }) => {
            if (!session) throw new Error('Bluesky session unavailable')
            if (!effectiveMastodonSession && !effectivePixelfedSession)
                throw new Error('No fediverse session available')

            return fetchCrossProtocolTimeline(session, effectiveMastodonSession, {
                cursors: pageParam,
                pixelfedSession: effectivePixelfedSession ?? undefined,
            })
        },
        initialPageParam: undefined as CrossProtocolCursors | undefined,
        getNextPageParam: (lastPage): CrossProtocolCursors | undefined => {
            const bluesky = lastPage.cursor ?? null
            const mastodon = lastPage.mastodonCursor ?? null
            const pixelfed = lastPage.pixelfedCursor ?? null

            // All exhausted — no more pages
            if (bluesky === null && mastodon === null && pixelfed === null) return undefined

            return { bluesky, mastodon, pixelfed }
        },
        enabled: (options?.enabled ?? true) && !!session && hasFediverse,
        staleTime: 60_000,
        gcTime: 5 * 60 * 1000,
        placeholderData: keepPreviousData,
    })

    const posts = useMemo(() => query.data?.pages.flatMap(page => page.posts) ?? [], [query.data])

    return {
        posts,
        queryKey,
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        isFetchingNextPage: query.isFetchingNextPage,
        hasNextPage: query.hasNextPage ?? false,
        fetchNextPage: query.fetchNextPage,
        error: query.error,
        refetch: query.refetch,
    }
}
